import React, { useEffect, useState } from 'react';
import { X, Loader2, Mail, Phone, MapPin, MessageSquarePlus, PhoneCall, Save } from 'lucide-react';
import { supabase } from '../services/supabase';
import { addNote, getActivities, logCall } from '../services/homeowners';
import { ACTIVITY_LABELS, PIPELINE_LABELS, PIPELINE_STAGES, type PipelineStage } from '../constants';
import type { Activity, HomeownerRecord, Property } from '../types';
import { useToast } from './Toast';

interface HomeownerDrawerProps {
  recordId: string;
  agentId: string;
  onClose: () => void;
  onChanged: () => void;
}

export default function HomeownerDrawer({ recordId, agentId, onClose, onChanged }: HomeownerDrawerProps) {
  const [record, setRecord] = useState<(HomeownerRecord & { property?: Property }) | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [notes, setNotes] = useState('');
  const [newNote, setNewNote] = useState('');
  const [callSummary, setCallSummary] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const refresh = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('doorsteps_homeowner_records')
      .select('*, property:doorsteps_properties(*)')
      .eq('id', recordId)
      .maybeSingle();
    setRecord(data as any);
    setNotes((data as any)?.notes ?? '');
    setActivities(await getActivities(recordId));
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, [recordId]);

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      await supabase
        .from('doorsteps_homeowner_records')
        .update({ notes, updated_at: new Date().toISOString() })
        .eq('id', recordId);
      toast.success('Notes saved.');
      onChanged();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    await addNote(recordId, agentId, newNote.trim());
    setNewNote('');
    refresh();
  };

  const handleLogCall = async () => {
    if (!callSummary.trim()) return;
    await logCall(recordId, agentId, callSummary.trim());
    setCallSummary('');
    refresh();
  };

  return (
    <div className="fixed inset-0 z-40 flex">
      <button
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        aria-label="Close"
      />
      <aside className="ml-auto relative w-full max-w-md bg-white shadow-2xl flex flex-col animate-slide-up">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Homeowner</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        {loading || !record ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="animate-spin text-slate-400" size={24} />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <MapPin size={14} />
                <span className="font-mono text-xs">{record.uprn}</span>
              </div>
              <div className="text-lg font-semibold text-slate-900">
                {record.property?.address_line_1 || '(unknown address)'}
              </div>
              <div className="text-sm text-slate-500">
                {[record.property?.town, record.property?.postcode].filter(Boolean).join(' · ')}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Stage</label>
              <select
                value={record.pipeline_stage}
                onChange={async (e) => {
                  const next = e.target.value as PipelineStage;
                  await supabase.rpc('doorsteps_set_pipeline_stage', { p_record_id: recordId, p_stage: next });
                  refresh();
                  onChanged();
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400"
              >
                {PIPELINE_STAGES.map((s) => (
                  <option key={s} value={s}>{PIPELINE_LABELS[s]}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg text-slate-700">
                <Phone size={14} className="text-slate-400" />
                <span className="truncate">{record.contact_phone || '—'}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg text-slate-700">
                <Mail size={14} className="text-slate-400" />
                <span className="truncate">{record.contact_email || '—'}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Background, preferences, history…"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400 resize-none"
              />
              <button
                onClick={handleSaveNotes}
                disabled={saving}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-xs font-medium"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save notes
              </button>
            </div>

            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Quick activity note…"
                  className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400"
                />
                <button
                  onClick={handleAddNote}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-medium flex items-center gap-1"
                >
                  <MessageSquarePlus size={12} /> Add
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  value={callSummary}
                  onChange={(e) => setCallSummary(e.target.value)}
                  placeholder="Log a call…"
                  className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400"
                />
                <button
                  onClick={handleLogCall}
                  className="px-3 py-1.5 bg-slate-700 hover:bg-slate-800 text-white rounded-lg text-xs font-medium flex items-center gap-1"
                >
                  <PhoneCall size={12} /> Log
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Activity</label>
              <ul className="space-y-2">
                {activities.map((a) => (
                  <li key={a.id} className="px-3 py-2 bg-slate-50 rounded-lg text-sm">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="font-medium text-slate-700">{ACTIVITY_LABELS[a.type] ?? a.type}</span>
                      <span className="text-xs text-slate-400">
                        {new Date(a.created_at).toLocaleString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    {a.payload && Object.keys(a.payload).length > 0 && (
                      <div className="text-xs text-slate-500 break-words">
                        {(a.payload as any).text ||
                         (a.payload as any).summary ||
                         (a.payload as any).message ||
                         JSON.stringify(a.payload)}
                      </div>
                    )}
                  </li>
                ))}
                {activities.length === 0 && (
                  <li className="text-sm text-slate-400 px-3 py-2">No activity yet.</li>
                )}
              </ul>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
